// Tears down stale Create React App service workers + caches left behind on
// clients that registered the now-removed offline support. See
// docs/fixes/stale-service-worker-cache.md (#4009). The matching removal task
// is docs/fixes/remove-service-worker-kill-switch.md (#4010).

const RELOAD_FLAG_KEY = 'swKillSwitchReloaded';

function readReloadFlag(): boolean | 'unavailable' {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_KEY) === '1';
  } catch {
    return 'unavailable';
  }
}

function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    // ignore
  }
}

function writeReloadFlag(): boolean {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

export async function runServiceWorkerKillSwitch(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  const flag = readReloadFlag();
  if (flag === 'unavailable') {
    // Without sessionStorage we can't guard against a reload loop, so don't
    // start one. Affected clients (private/locked-down storage) remain stuck
    // until they hard-reload or open incognito.
    return;
  }
  if (flag) {
    clearReloadFlag();
    return;
  }

  let registrations: readonly ServiceWorkerRegistration[];
  try {
    registrations = await navigator.serviceWorker.getRegistrations();
  } catch {
    return;
  }
  if (registrations.length === 0) {
    return;
  }

  await Promise.all(registrations.map(r => r.unregister().catch(() => false)));

  if (typeof caches !== 'undefined') {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k).catch(() => false)));
    } catch {
      // best-effort; if cache eviction fails the unregister above is still enough
      // to stop the SW from intercepting the next navigation.
    }
  }

  if (!writeReloadFlag()) {
    return;
  }

  window.location.reload();
  // reload() schedules navigation but doesn't stop script execution; hang so
  // the caller doesn't mount React in the window before the reload fires.
  await new Promise<never>(() => {});
}
